/**
 * Data Source Utilities
 * Functions for managing data sources in FormBuilder
 */
import {
  DataSourceConfig,
  DataSourceType,
  FieldOption,
  DataSourceState,
  FunctionDataSourceConfig,
  UrlDataSourceConfig
} from '../types'

/**
 * Load data source data
 * @param dataSource Data source configuration
 * @param formValues Form values, used for handling dependencies
 * @returns Returns a Promise containing the loaded data
 */
export const loadDataSource = async (
  dataSource: DataSourceConfig,
  formValues: Record<string, any>
): Promise<any[]> => {
  try {
    // Call different loading methods based on data source type
    let data: any

    if (dataSource.type === DataSourceType.FUNCTION) {
      data = await loadFunctionDataSource(dataSource, formValues)
    } else {
      data = await loadUrlDataSource(
        dataSource as UrlDataSourceConfig,
        formValues
      )
    }

    // Return raw data without applying transformation
    // Transform will be applied separately after caching
    return data
  } catch (error) {
    // Call error handler function (if any)
    if (dataSource.errorHandler) {
      dataSource.errorHandler(error)
    }

    // Rethrow error for caller to handle
    throw error
  }
}

/**
 * Load function type data source
 * @param dataSource Function type data source configuration
 * @param formValues Form values
 * @returns Returns a Promise containing the loaded data
 */
export const loadFunctionDataSource = async (
  dataSource: FunctionDataSourceConfig,
  formValues: Record<string, any>
): Promise<any[]> => {
  // Process function parameters
  let params: any = {}

  if (dataSource.params) {
    params = { ...dataSource.params }

    // Replace form value references in parameters, e.g. {fieldName}
    Object.keys(params).forEach(key => {
      const value = params[key]
      if (typeof value === 'string' && value.match(/^\{.*\}$/)) {
        const fieldName = value.slice(1, -1)
        const fieldValue = formValues[fieldName] || ''
        params[key] = fieldValue
      }
    })
  }

  // Call service function
  const result = await dataSource.service(params)
  return result
}

/**
 * Load URL type data source
 * @param dataSource URL type data source configuration
 * @param formValues Form values
 * @returns Returns a Promise containing the loaded data
 */
export const loadUrlDataSource = async (
  dataSource: UrlDataSourceConfig,
  formValues: Record<string, any>
): Promise<any[]> => {
  // Build request URL and parameters
  const url = new URL(dataSource.url)

  // Process URL parameters
  if (dataSource.params) {
    const params = { ...dataSource.params }

    // Replace form value references in parameters, e.g. {fieldName}
    Object.keys(params).forEach(key => {
      const value = params[key]
      if (typeof value === 'string' && value.match(/^\{.*\}$/)) {
        const fieldName = value.slice(1, -1)
        params[key] = formValues[fieldName] || ''
      }
    })

    // Add parameters to URL
    Object.keys(params).forEach(key => {
      url.searchParams.append(key, params[key])
    })
  }

  // Build request options
  const options: RequestInit = {
    method: dataSource.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...dataSource.headers
    }
  }

  // Process request body
  if (
    dataSource.body &&
    (options.method === 'POST' || options.method === 'PUT')
  ) {
    const body = { ...dataSource.body }

    // Replace form value references in request body
    Object.keys(body).forEach(key => {
      const value = body[key]
      if (typeof value === 'string' && value.match(/^\{.*\}$/)) {
        const fieldName = value.slice(1, -1)
        body[key] = formValues[fieldName] || ''
      }
    })

    options.body = JSON.stringify(body)
  }

  // Send request
  const response = await fetch(url.toString(), options)

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}: ${response.statusText}`)
  }

  // Parse response
  return await response.json()
}

/**
 * Convert data source data to options format
 * @param data Data source data
 * @param valueField Value field name
 * @param labelField Label field name
 * @returns Converted options array
 */
export const convertToOptions = (
  data: any[] | null,
  valueField: string = 'value',
  labelField: string = 'label'
): FieldOption[] => {
  if (!data || !Array.isArray(data)) {
    return []
  }

  return data.map(item => ({
    value: item[valueField] ?? '',
    label: item[labelField] ?? ''
  }))
}

/**
 * Check if data source needs to be reloaded
 * @param dataSource Data source configuration
 * @param prevValues Previous form values
 * @param currentValues Current form values
 * @returns Whether reload is needed
 */
export const shouldReloadDataSource = (
  dataSource: DataSourceConfig,
  prevValues: Record<string, any>,
  currentValues: Record<string, any>
): boolean => {
  // If no dependent fields, no need to reload
  if (!dataSource.dependencies || dataSource.dependencies.length === 0) {
    return false
  }

  // Check if dependent fields have changed
  const shouldReload = dataSource.dependencies.some(field => {
    const prevValue = prevValues[field]
    const currentValue = currentValues[field]

    // Process different value types
    if (typeof currentValue === 'boolean' || typeof prevValue === 'boolean') {
      return prevValue !== currentValue
    }

    const hasChanged = prevValues[field] !== currentValues[field]
    return hasChanged
  })
  return shouldReload
}

/**
 * Create initial data source state
 * @returns Initial data source state
 */
export const createInitialDataSourceState = (): DataSourceState => ({
  loading: false,
  data: null,
  error: null,
  options: []
})

/**
 * Get data source dependencies
 * @param dataSources Data source configuration array
 * @returns Array of all dependent fields
 */
export const getDataSourceDependencies = (
  dataSources: DataSourceConfig[] = []
): string[] => {
  const dependencies: string[] = []

  dataSources.forEach(dataSource => {
    if (dataSource.dependencies && dataSource.dependencies.length > 0) {
      dependencies.push(...dataSource.dependencies)
    }
  })

  // Remove duplicates
  return [...new Set(dependencies)]
}

/**
 * Apply data source transformation
 * @param dataSource Data source configuration
 * @param rawData Raw data from API or cache
 * @returns Transformed data
 */
export const applyDataSourceTransform = (
  dataSource: DataSourceConfig,
  rawData: any
): any => {
  // Apply data transformation if configured
  if (dataSource.transform) {
    return dataSource.transform(rawData)
  }

  // Return raw data if no transformation is configured
  return rawData
}
